import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getHumanSupport, saveHumanSupport } from '../../services/configService';

export default function HumanSupportEmail() {
  const { user } = useAuth();
  const [email, setEmail]     = useState('');
  const [saved, setSaved]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    getHumanSupport()
      .then(({ config }) => {
        const e = config?.supportEmail || '';
        setEmail(e);
        setSaved(e);
      })
      .catch(() => setError('Failed to load human support settings.'))
      .finally(() => setLoading(false));
  }, [user]);

  const isDirty    = email !== (saved ?? '');
  const isValid    = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const showSaving = saving;
  const showSaved  = !isDirty && !saving && saved !== null;

  async function handleSave() {
    if (!isValid) return;
    setError('');
    setSaving(true);
    try {
      await saveHumanSupport(email);
      setSaved(email);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#475569', fontSize: 13 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
        style={{ animation: 'spin 0.8s linear infinite' }}>
        <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
      </svg>
      Loading…
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.65 }}>
        When the AI cannot resolve a query, it will offer to escalate to a human agent at this email address.
      </p>

      <div>
        <p style={{ fontSize: 11.5, color: '#475569', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>Support Email</p>
        <input
          type="email"
          value={email}
          onChange={e => { setEmail(e.target.value); setError(''); }}
          placeholder="support@yourcompany.com"
          style={{
            width: '100%', padding: '9px 14px', borderRadius: 9, boxSizing: 'border-box',
            background: 'rgba(15,23,42,0.7)',
            border: `1.5px solid ${email.length > 0 && !isValid ? 'rgba(239,68,68,0.5)' : 'rgba(71,158,195,0.2)'}`,
            color: '#e2e8f0', fontSize: 13.5, outline: 'none',
            transition: 'border-color .15s',
          }}
          onFocus={e => { e.target.style.borderColor = email.length > 0 && !isValid ? 'rgba(239,68,68,0.6)' : 'rgba(71,158,195,0.55)'; }}
          onBlur={e => { e.target.style.borderColor = email.length > 0 && !isValid ? 'rgba(239,68,68,0.5)' : 'rgba(71,158,195,0.2)'; }}
        />
        {email.length > 0 && !isValid && (
          <p style={{ fontSize: 12, color: '#f87171', marginTop: 5 }}>Please enter a valid email address.</p>
        )}
      </div>

      {error && (
        <p style={{ fontSize: 13, color: '#f87171', margin: 0, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          {error}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!isDirty || saving || !isValid}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 24px', borderRadius: 9, border: 'none',
            cursor: !isDirty || saving || !isValid ? 'not-allowed' : 'pointer',
            background: !isDirty || saving || !isValid ? 'rgba(30,41,59,0.6)' : 'linear-gradient(135deg,#479EC3,#22D3EE)',
            color: !isDirty || saving || !isValid ? '#334155' : '#fff',
            fontSize: 14, fontWeight: 700,
            transition: 'background .2s',
          }}
        >
          Save
        </button>

        <span style={{
          fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, minWidth: 72,
          color: showSaving ? '#64748b' : '#10b981',
          opacity: showSaving || showSaved ? 1 : 0,
          transition: 'opacity .2s',
          pointerEvents: 'none',
        }}>
          {showSaving ? (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
                <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
              </svg>
              Saving…
            </>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0 }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Saved
            </>
          )}
        </span>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
